import { forwardRef, type HTMLAttributes } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { ArrowUpRight, ArrowDownRight, type LucideIcon } from 'lucide-react';
import { cn } from '../lib/cn';

const statCardVariants = cva(
  "relative overflow-hidden rounded-xl border p-6 hover:shadow-md hover:-translate-y-0.5 transition-all duration-200",
  {
    variants: {
      variant: {
        default: "bg-card border-border",
        primary: "bg-primary/5 border-primary/20",
        success: "bg-success/5 border-success/20",
        warning: "bg-warning/5 border-warning/20",
        destructive: "bg-destructive/5 border-destructive/20",
      }
    },
    defaultVariants: {
      variant: "default",
    }
  }
);

const iconWrapperVariants = cva(
  "mb-4 flex h-10 w-10 items-center justify-center rounded-lg",
  {
    variants: {
      variant: {
        default: "bg-muted text-muted-foreground",
        primary: "bg-primary/10 text-primary",
        success: "bg-success/10 text-success",
        warning: "bg-warning/10 text-warning",
        destructive: "bg-destructive/10 text-destructive",
      }
    },
    defaultVariants: {
      variant: "default",
    }
  }
);

export interface StatCardProps extends HTMLAttributes<HTMLDivElement>, VariantProps<typeof statCardVariants> {
  title: string;
  value: string | number;
  icon?: LucideIcon;
  delta?: { value: number; label?: string };
}

export const StatCard = forwardRef<HTMLDivElement, StatCardProps>(
  ({ className, variant, title, value, icon: Icon, delta, ...props }, ref) => {
    return (
      <div ref={ref} className={cn(statCardVariants({ variant }), className)} {...props}>
        <div className="flex flex-col">
          {Icon && (
            <div className={cn(iconWrapperVariants({ variant }))}>
              <Icon className="h-5 w-5" />
            </div>
          )}
          <h3 className="text-sm font-medium text-muted-foreground">{title}</h3>
          <div className="mt-2 flex items-baseline gap-2">
            <div className="text-2xl font-bold tracking-tight text-foreground">{value}</div>
            {delta && (
              <div
                className={cn(
                  "ml-auto flex items-center text-sm font-medium",
                  delta.value > 0 ? "text-success" : delta.value < 0 ? "text-destructive" : "text-muted-foreground"
                )}
              >
                {delta.value > 0 ? (
                  <ArrowUpRight className="mr-1 h-4 w-4" />
                ) : delta.value < 0 ? (
                  <ArrowDownRight className="mr-1 h-4 w-4" />
                ) : null}
                <span>{Math.abs(delta.value)}%</span>
                {delta.label && <span className="ml-1 text-muted-foreground">{delta.label}</span>}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }
);
StatCard.displayName = "StatCard";
